import React, { useState, useEffect, useRef } from "react";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import useApi from "../utils/useApi";
import { useLoading } from "../context/LoadingContext";

const EditUserModal = ({ show, onClose, userId, fetchUsers }) => {
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const modalRef = useRef(null);
  const bootstrapModalRef = useRef(null); // Persistent Bootstrap Modal instance

  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    user_password: "",
  });

  // ✅ Manage Bootstrap Modal Instance
  useEffect(() => {
    if (modalRef.current) {
      bootstrapModalRef.current = new Modal(modalRef.current, { keyboard: false });

      if (show) {
        bootstrapModalRef.current.show();
      }
    }
  }, [show]);

  // ✅ Fetch user details when userId changes
  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  // ✅ Cleanup Bootstrap Modal when closing
  useEffect(() => {
    return () => {
      if (bootstrapModalRef.current) {
        bootstrapModalRef.current.hide();
        bootstrapModalRef.current.dispose(); // Dispose modal to prevent multiple instances
      }
    };
  }, []);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await callApi({
        endpoint: `/admin/generate-admin/show/${userId}`,
      });

      setFormData({
        user_name: response.data.user_name,
        user_email: response.data.user_email,
        user_password: "",
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load user details.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const bodyData = { ...formData };
      if (!bodyData.user_password) {
        delete bodyData.user_password;
      }

      await callApi({
        endpoint: `/admin/generate-admin/update/${userId}`,
        method: "PUT",
        data: bodyData,
      });

      Swal.fire("Updated!", "User details have been updated.", "success");
      fetchUsers();
      closeModal(); // ✅ Close Modal Properly
    } catch (error) {
      Swal.fire("Error", "Failed to update user. Try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    if (bootstrapModalRef.current) {
      bootstrapModalRef.current.hide(); // ✅ Hide Modal
    }
    onClose(); // Call parent function to reset modal state
  };

  return (
    <div className="modal fade global_modal" ref={modalRef} id="editUserModal" tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit User</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={closeModal}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Username</label>
                <input type="text" name="user_name" className="form-control" value={formData.user_name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" name="user_email" className="form-control" value={formData.user_email} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Password (leave empty to keep the same)</label>
                <input type="password" name="user_password" className="form-control" value={formData.user_password} onChange={handleChange} />
              </div>
              <br />
              <div className="btn_holder">
                <button type="submit" className="btn btn-primary">Update</button>
                <button type="button" className="btn btn-primary orange_btn" data-bs-dismiss="modal" onClick={closeModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
